import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, ViewChild, HostListener, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import YTPlayer from 'youtube-player';
import VPlayer from '@vimeo/player';
import * as i1 from '@angular/platform-browser';
import * as i2 from '@angular/common/http';

function noop() { } // eslint-disable-line @typescript-eslint/no-empty-function

function convertToYouTubeEvent(event) {
    if (event === 'play')
        return 1;
    if (event === 'pause')
        return 2;
    if (event === 'end')
        return 0;
    return null;
}
class YouTubePlayer {
    constructor(player) {
        this.player = player;
        if (!this.player)
            throw Error('Unable to create youtube video player');
    }
    get isPlaying() {
        return this.player.getPlayerState() === 1;
    }
    play() {
        this.player.playVideo();
    }
    pause() {
        this.player.pauseVideo();
    }
    rewind() {
        this.player.seekTo(0);
    }
    destroy() {
        this.player.destroy();
    }
    on(event, callback) {
        const key = convertToYouTubeEvent(event);
        if (!key)
            return noop;
        const listener = this.player.on('stateChange', ({ data }) => {
            if (key !== data)
                return;
            callback(this.player);
        });
        return () => this.player.off(listener);
    }
}
function supports$2(source) {
    return source === 'youtube';
}
function preview$2(id) {
    return Promise.resolve(`//img.youtube.com/vi/${id}/hqdefault.jpg`);
}
function url$2(options) {
    if (!options?.id)
        return null;
    const controls = options.controls ? 1 : 0;
    let url = `//www.youtube.com/embed/${options.id}?rel=0&disablekb=1&iv_load_policy=3&modestbranding=1&showinfo=0&autohide=1&fs=0&enablejsapi=1&controls=${controls}`;
    if (options.start)
        url += `&start=${options.start}`;
    const { origin } = window.location;
    return origin ? `${url}&origin=${origin}` : url;
}
function create$2(options) {
    if (!options?.target) {
        return Promise.reject(new Error('target element was not provided.'));
    }
    return new Promise((resolve, reject) => {
        try {
            const youtube = YTPlayer(options.target, { videoId: options.id });
            const player = new YouTubePlayer(youtube);
            youtube.on('error', () => {
                //youtube.off(error);
                //youtube.off(ready); // eslint-disable-line @typescript-eslint/no-use-before-define
                reject(new Error('there was a problem trying to initialize youtube player'));
            });
            youtube.on('ready', () => {
                //youtube.off(ready);
                //youtube.off(error);
                resolve(player);
            });
        }
        catch (e) {
            reject(e);
        }
    });
}

function convertToVimeoEvent(event) {
    if (event === 'play' || event === 'pause')
        return event;
    if (event === 'end')
        return 'ended';
    return null;
}
class VimeoPlayer {
    constructor(player) {
        this.player = player;
        this.playing = false;
        if (!this.player)
            throw Error('Unable to create vimeo video player');
        this.setPlayingTrue = this.setPlayingTrue.bind(this);
        this.setPlayingFalse = this.setPlayingFalse.bind(this);
        this.player.on('playing', this.setPlayingTrue);
        this.player.on('pause', this.setPlayingFalse);
        this.player.on('ended', this.setPlayingFalse);
    }
    setPlayingTrue() {
        this.playing = true;
    }
    setPlayingFalse() {
        this.playing = false;
    }
    get isPlaying() {
        return this.playing;
    }
    play() {
        this.player.play().catch((e) => console.log(e));
    }
    pause() {
        this.player.pause();
    }
    rewind() {
        this.player.setCurrentTime(0);
    }
    destroy() {
        this.player.off('playing', this.setPlayingTrue);
        this.player.off('pause', this.setPlayingFalse);
        this.player.off('ended', this.setPlayingFalse);
        this.player.destroy();
    }
    on(event, callback) {
        const key = convertToVimeoEvent(event);
        if (!key)
            return noop;
        const fn = () => callback(this);
        this.player.on(key, fn);
        return () => this.player.off(key, fn);
    }
}
function supports$1(source) {
    return source === 'vimeo';
}
function preview$1(id, http) {
    if (!http || !id)
        return Promise.resolve(null);
    return (http
        .get(`//vimeo.com/api/v2/video/${id}.json`)
        .toPromise()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((data) => {
        const [video] = data;
        return (video?.thumbnail_large ??
            video?.thumbnail_medium ??
            video?.thumbnail_small);
    }));
}
function url$1(options) {
    if (!options?.id)
        return null;
    let url = `//player.vimeo.com/video/${options.id}?badge=0&byline=0&portrait=0&title=0`;
    if (options.start)
        url += `#t=${options.start}s`;
    const { origin } = window.location;
    return origin ? `${url}&origin=${origin}` : url;
}
function create$1(options) {
    if (!options?.target) {
        return Promise.reject(new Error('target element was not provided.'));
    }
    return new Promise((resolve, reject) => {
        try {
            const videoId = isNaN(Number(options.id)) ? void 0 : Number(options.id);
            const vimeo = new VPlayer(options.target, { id: videoId });
            const player = new VimeoPlayer(vimeo);
            vimeo
                .ready()
                .then(() => resolve(player))
                .catch(reject);
        }
        catch (e) {
            reject(e);
        }
    });
}

const WISTIA_SCRIPT_ID = 'wistia-sdk';
function ensureScript() {
    if (!!document.getElementById(WISTIA_SCRIPT_ID))
        return;
    const body = document.body || document.getElementsByTagName('body')[0];
    const script = document.createElement('script');
    script.id = WISTIA_SCRIPT_ID;
    script.async = true;
    script.type = 'text/javascript';
    script.src = `//fast.wistia.net/assets/external/E-v1.js`;
    body.appendChild(script);
}
function queue() {
    if (!window['_wq'])
        window['_wq'] = [];
    return window['_wq'];
}
function convertToWistiaEvent(event) {
    if (event !== 'play' && event !== 'pause' && event !== 'end')
        return null;
    return null;
}
class WistiaPlayer {
    constructor(video) {
        this.video = video;
        if (!this.video)
            throw Error('Unable to create wistia video player');
    }
    get isPlaying() {
        return this.video.state() === 'playing';
    }
    play() {
        this.video.play();
    }
    pause() {
        this.video.pause();
    }
    rewind() {
        this.video.time(0);
    }
    destroy() {
        this.video.remove();
    }
    on(event, callback) {
        const key = convertToWistiaEvent(event);
        if (!key)
            return noop;
        const fn = () => callback(this);
        this.video.bind(key, fn);
        return () => this.video.unbind(key, fn);
    }
}
function supports(source) {
    return source === 'wistia';
}
function preview() {
    // XXX: We don't care about Wistia that much
    return Promise.resolve(`//slidebean.s3.amazonaws.com/wistia-placeholder.png`);
}
function url(id) {
    if (!id)
        return null;
    const url = `//fast.wistia.net/embed/iframe/${id}?chromeless=true&fullscreenButton=false`;
    const { origin } = window.location;
    return origin ? `${url}&origin=${origin}` : url;
}
function create(id) {
    ensureScript();
    return new Promise((resolve, reject) => {
        queue().push({
            id,
            onReady: (video) => {
                try {
                    resolve(new WistiaPlayer(video));
                }
                catch (e) {
                    reject(e);
                }
            },
        });
    });
}

class VideoPlayerComponent {
    get isReady() {
        return !!this.player;
    }
    get hasUrl() {
        return !!this.videoUrl;
    }
    constructor(domSanitizer, changeDetectorRef, http) {
        this.domSanitizer = domSanitizer;
        this.changeDetectorRef = changeDetectorRef;
        this.http = http;
        this.onVideoEnded = new EventEmitter();
        this.isLoading = false;
        this.mouseIsOverVideo = false;
    }
    ngOnChanges() {
        this.clearPlayer();
        this.reloadVideoUrl();
        this.reloadPreview();
    }
    ngOnDestroy() {
        this.clearPlayer();
    }
    reloadVideoUrl() {
        const url$3 = supports$2(this.videoSource)
            ? url$2({
                id: this.videoId,
                start: this.videoPosition,
                controls: this.showControls,
            })
            : supports$1(this.videoSource)
                ? url$1({ id: this.videoId, start: this.videoPosition })
                : supports(this.videoSource)
                    ? url(this.videoId)
                    : null;
        this.videoUrl = url$3
            ? this.domSanitizer.bypassSecurityTrustResourceUrl(url$3)
            : null;
        this.changeDetectorRef.detectChanges();
    }
    reloadPreview() {
        return (supports$2(this.videoSource)
            ? preview$2(this.videoId)
            : supports$1(this.videoSource)
                ? preview$1(this.videoId, this.http)
                : supports(this.videoSource)
                    ? preview()
                    : Promise.resolve(null))
            .then(url => {
            this.videoPreviewStyle = url
                ? this.domSanitizer.bypassSecurityTrustStyle(`url(${url})`)
                : null;
            this.changeDetectorRef.detectChanges();
        })
            .catch(() => {
            this.videoPreviewStyle = null;
        });
    }
    getPlayer() {
        return Promise.resolve(this.player || this.loadPlayer());
    }
    isPlaying() {
        return this.getPlayer()
            .then(player => player.isPlaying)
            .catch(() => false);
    }
    async loadPlayer() {
        if (!this.isLoading) {
            this.isLoading = true;
            this.changeDetectorRef.detectChanges();
        }
        try {
            this.player = await (supports$2(this.videoSource)
                ? create$2({
                    target: this.content.nativeElement,
                    id: this.videoId,
                })
                : supports$1(this.videoSource)
                    ? create$1({
                        target: this.content.nativeElement,
                        id: this.videoId,
                    })
                    : supports(this.videoSource)
                        ? create(this.videoId)
                        : null);
            if (!this.player) {
                throw new Error(`Video source ${this.videoSource} not supported.`);
            }
            this.unsubscribe = this.player.on('end', () => this.onVideoEnded.emit(this));
            return this.player;
        }
        finally {
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
        }
    }
    play() {
        this.getPlayer()
            .then(player => player.play())
            .catch(error => console.error('Could not play the video', error));
    }
    pause() {
        this.getPlayer()
            .then(player => player.pause())
            .catch(error => console.error('Could not pause the video', error));
    }
    rewind() {
        this.getPlayer()
            .then(player => player.rewind())
            .catch(error => console.error('Could not rewind the video', error));
    }
    togglePlayPause() {
        return this.isPlaying().then(isPlaying => isPlaying ? this.pause() : this.play());
    }
    // XXX: Return the focus to the window when the iframe is clicked,
    // so that the player navigation continues to work.
    onWindowBlur() {
        if (this.mouseIsOverVideo) {
            setTimeout(() => window.focus());
        }
    }
    onVideoMouseOver() {
        this.mouseIsOverVideo = true;
    }
    onVideoMouseOut() {
        this.mouseIsOverVideo = false;
    }
    clearPlayer() {
        if (this.unsubscribe)
            this.unsubscribe();
        if (this.player) {
            this.player.destroy();
            this.player = null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: VideoPlayerComponent, deps: [{ token: i1.DomSanitizer }, { token: i0.ChangeDetectorRef }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: VideoPlayerComponent, selector: "sb-video-player", inputs: { videoId: "videoId", videoSource: "videoSource", videoPosition: "videoPosition", showControls: "showControls" }, outputs: { onVideoEnded: "onVideoEnded" }, host: { listeners: { "window:blur": "onWindowBlur()" } }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<i *ngIf=\"!videoUrl\" class=\"far fa-play-circle video__placeholder\"></i>\n\n<ng-container *ngIf=\"!!videoUrl\">\n  <div\n    *ngIf=\"!!videoPreviewStyle\"\n    class=\"video__preview\"\n    [style.background-image]=\"videoPreviewStyle\"\n    (click)=\"play()\"\n  ></div>\n  <iframe\n    #content\n    *ngIf=\"isReady || isLoading\"\n    frameborder=\"0\"\n    class=\"video__content\"\n    [src]=\"videoUrl\"\n    [class.wistia_embed]=\"videoSource === 'wistia'\"\n    (mouseover)=\"onVideoMouseOver()\"\n    (mouseoout)=\"onVideoMouseOut()\"\n  ></iframe>\n</ng-container>\n", styles: [":host{display:block;pointer-events:auto;position:relative;width:100%;height:0;padding-bottom:62.5%;background:#000}.video__placeholder{color:#fff;opacity:.5;font-size:4em;position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)}.video__content,.video__preview{position:absolute;top:0;left:0;width:100%;height:100%;cursor:pointer}.video__preview{background:no-repeat center/contain}\n"], dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: VideoPlayerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sb-video-player', template: "<i *ngIf=\"!videoUrl\" class=\"far fa-play-circle video__placeholder\"></i>\n\n<ng-container *ngIf=\"!!videoUrl\">\n  <div\n    *ngIf=\"!!videoPreviewStyle\"\n    class=\"video__preview\"\n    [style.background-image]=\"videoPreviewStyle\"\n    (click)=\"play()\"\n  ></div>\n  <iframe\n    #content\n    *ngIf=\"isReady || isLoading\"\n    frameborder=\"0\"\n    class=\"video__content\"\n    [src]=\"videoUrl\"\n    [class.wistia_embed]=\"videoSource === 'wistia'\"\n    (mouseover)=\"onVideoMouseOver()\"\n    (mouseoout)=\"onVideoMouseOut()\"\n  ></iframe>\n</ng-container>\n", styles: [":host{display:block;pointer-events:auto;position:relative;width:100%;height:0;padding-bottom:62.5%;background:#000}.video__placeholder{color:#fff;opacity:.5;font-size:4em;position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)}.video__content,.video__preview{position:absolute;top:0;left:0;width:100%;height:100%;cursor:pointer}.video__preview{background:no-repeat center/contain}\n"] }]
        }], ctorParameters: () => [{ type: i1.DomSanitizer }, { type: i0.ChangeDetectorRef }, { type: i2.HttpClient }], propDecorators: { videoId: [{
                type: Input
            }], videoSource: [{
                type: Input
            }], videoPosition: [{
                type: Input
            }], showControls: [{
                type: Input
            }], onVideoEnded: [{
                type: Output
            }], content: [{
                type: ViewChild,
                args: ['content', { static: false }]
            }], onWindowBlur: [{
                type: HostListener,
                args: ['window:blur']
            }] } });

class VideoModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: VideoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: VideoModule, declarations: [VideoPlayerComponent], imports: [CommonModule], exports: [VideoPlayerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: VideoModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: VideoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [VideoPlayerComponent],
                    exports: [VideoPlayerComponent],
                }]
        }] });

const REGEX_VIDEO_YOUTUBE = /(?:(?:youtu|yu2|y2u)\.be\/([a-zA-Z0-9_\-]+))|(?:youtube.com\/embed\/([a-zA-Z0-9_\-]+))|(?:youtube.com\/watch?.*v=([a-zA-Z0-9_\-]+))/i;
const REGEX_VIDEO_YOUTUBE_POSITION = /(?:star)?t=([0-9]+)(?:m([0-9]+))?/i;
const REGEX_VIDEO_VIMEO = /vimeo.com\/(?:video\/)?([0-9]+)/i;
const REGEX_VIDEO_WISTIA = /wistia\.(?:com|net)\/(?:medias\/|embed\/|iframe\/)*([a-zA-Z0-9_\-]+)/i;
class VideoUrl {
    constructor(url) {
        this._url = url;
    }
    getVideoSource() {
        if (REGEX_VIDEO_VIMEO.test(this._url))
            return 'vimeo';
        if (REGEX_VIDEO_YOUTUBE.test(this._url))
            return 'youtube';
        if (REGEX_VIDEO_WISTIA.test(this._url))
            return 'wistia';
        return null;
    }
    getVideoId() {
        const regexes = [
            REGEX_VIDEO_VIMEO,
            REGEX_VIDEO_YOUTUBE,
            REGEX_VIDEO_WISTIA,
        ];
        for (let r = 0; r < regexes.length; r++) {
            const matches = regexes[r].exec(this._url);
            if (matches && matches.length) {
                for (let i = 1; i < matches.length; i++) {
                    if (matches[i]) {
                        return matches[i];
                    }
                }
            }
        }
        return null;
    }
    getVideoPosition(videoSource) {
        if (videoSource === 'youtube') {
            const matches = REGEX_VIDEO_YOUTUBE_POSITION.exec(this._url);
            if (matches && matches.length) {
                let seconds = parseInt(matches[1], 10);
                if (matches[2]) {
                    seconds *= 60;
                    seconds += parseInt(matches[2], 10);
                }
                return seconds;
            }
        }
        return null;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { VideoModule, VideoPlayerComponent, VideoUrl };
